import { type DragEvent, useCallback } from "react";
import { useEventListener } from "@web-monorepo/hooks";
import { uniqBy } from "es-toolkit";
import type { FileInputSource, FileRejection, FileRejectionReason, RejectedFileInfo } from "../types";

export const mergeFileLists = (...lists: (FileList | File[] | null | undefined)[]): FileList => {
  const dt = new DataTransfer();
  const files = uniqBy(
    lists.filter(Boolean).flatMap((list) => Array.from(list)),
    (file) => file.name,
  );
  for (const file of files) {
    dt.items.add(file);
  }

  return dt.files;
};

/**
 * A collision with something already in `existing` is a harmless re-affirmation and isn't
 * reported; a collision between two INCOMING files never competed for a cap slot at all.
 */
export const splitByCapacity = (existing: File[], incoming: File[], maxAllowed: number) => {
  const existingNames = new Set(existing.map((file) => file.name));
  const merged = uniqBy([...existing, ...incoming], (file) => file.name);
  const kept = merged.slice(0, maxAllowed);
  const mergedSet = new Set(merged);
  const keptSet = new Set(kept);

  const duplicates: File[] = [];
  const tooMany: File[] = [];

  for (const file of incoming) {
    if (existingNames.has(file.name) || keptSet.has(file)) continue;
    (mergedSet.has(file) ? tooMany : duplicates).push(file);
  }

  return { kept, duplicates, tooMany };
};

/**
 * Splits files by why they can't be accepted, so callers can report each reason separately.
 * A file lands in exactly one bucket, checked extension → name length → size, so a file failing
 * several is only reported under the first.
 */
export const partitionFiles = (
  files: File[],
  allowedFileExtensions: string[],
  constraints: {
    /** When omitted, name length is not checked and `invalidName` is always empty. */
    maxFilenameLength?: number;
    /** When omitted, size is not checked and `tooLarge` is always empty. */
    maxFileSizeBytes?: number;
  } = {},
) => {
  const { maxFilenameLength, maxFileSizeBytes } = constraints;

  return files.reduce(
    (acc, file) => {
      if (!allowedFileExtensions.includes(getExtension(file.name.toLowerCase()))) {
        acc.invalid.push(file);
      } else if (maxFilenameLength !== undefined && file.name.length > maxFilenameLength) {
        acc.invalidName.push(file);
      } else if (maxFileSizeBytes !== undefined && file.size > maxFileSizeBytes) {
        acc.tooLarge.push(file);
      } else {
        acc.valid.push(file);
      }

      return acc;
    },
    { valid: [] as File[], invalid: [] as File[], invalidName: [] as File[], tooLarge: [] as File[] },
  );
};

const getExtension = (fileName: string | undefined) => (fileName ? `.${fileName.split(".").pop()}` : "");

const MAX_MIME_TYPE_LENGTH = 128;

/** Diverges from `partitionFiles`'s extension split, gated by the allowlist — see `RejectedFileInfo.extension` for why. */
export const toRejectedFileInfo = (file: File, allowedFileExtensions: string[]): RejectedFileInfo => {
  const name = file.name.toLowerCase();
  const extension = name.includes(".") ? getExtension(name) : "";

  return {
    extension: allowedFileExtensions.includes(extension) ? extension : "",
    size: file.size,
    // `File.type` is unvalidated by the Blob spec, so bound it like the rest of this payload.
    mimeType: file.type.slice(0, MAX_MIME_TYPE_LENGTH),
  };
};

export const MAX_REJECTED_FILES_REPORTED = 10;

export const toFileRejection = (
  reason: FileRejectionReason,
  source: FileInputSource,
  files: File[],
  allowedFileExtensions: string[],
): FileRejection => ({
  reason,
  source,
  files: files.slice(0, MAX_REJECTED_FILES_REPORTED).map((file) => toRejectedFileInfo(file, allowedFileExtensions)),
  totalCount: files.length,
});

export const preventDefault = (event: DragEvent<HTMLFormElement>) => event.preventDefault();
export const useUpdatedDragStateHandler = (
  newState: "enter" | "leave" | "drop",
  handler: (state: "enter" | "leave" | "drop") => void,
) =>
  useCallback(
    (event: DragEvent<HTMLFormElement>) => {
      event.preventDefault();
      handler(newState);
    },
    [handler, newState],
  );

export const useOnPastedFiles = (callback: (files: File[]) => void) =>
  useEventListener(
    "paste",
    (event) => {
      const items = event.clipboardData?.items ?? [];
      const files: File[] = [];

      // Microsoft Word (and other Office apps) put both text and a bitmap rendering
      // of the selection on the clipboard. Skip image items when there's also text
      // so a copy-paste from Word doesn't get attached as a screenshot.
      const types = event.clipboardData?.types ?? [];
      const hasText = types.includes("text/plain") || types.includes("text/html");

      for (const item of items) {
        if (hasText && item.type.startsWith("image/")) continue;
        const file = item.getAsFile();
        if (file) {
          // file.name is readonly, and we rely on filenames being unique
          // this clones the file and appends a timestamp to the filename to allow for multiple pastes
          // (e.g. in MacOS, every pasted file is named "image.png")
          const renamed = new File([file], `${Date.now()}-${file.name}`, {
            type: file.type,
          });
          files.push(renamed);
        }
      }

      if (files.length > 0) {
        callback(files);
      }
    },
    globalThis.window,
  );
